import { parseRestockRows } from "../utility/stockRestockImport";
import { stockMovementValue } from "../utility/stockValuation";
import { useRef, useState } from "react";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { collection, doc, runTransaction } from "firebase/firestore";
import { db } from "../firebase";
import { getStockMovements } from "../utility/getStock";
import inventoryCountStockItem from "../utility/inventoryCountStockItem";
import restockStockItem from "../utility/restockStockItem";

const ITEMS_CSV_HEADER = ["ID", "Name", "Category ID", "Unit", "Minimum Alert", "On Hand", "Unit Cost HTVA", "Update"];
const MOVEMENTS_CSV_HEADER = ["ID", "Stock Item ID", "Delta", "Reason", "Created At", "Unit Cost HTVA", "Value HTVA"];
const CSV_SEPARATOR = ";";
const DATE_FORMAT = "YYYY-MM-DD HH:mm";
const IMPORT_TRANSACTION_CHUNK_SIZE = 200;
const STOCK_ITEMS_HEADER_TO_KEYS = {
  ID: "id",
  Name: "name",
  "Category ID": "categoryId",
  Unit: "unit",
  "Minimum Alert": "minAlert",
  "On Hand": "onHand",
  "Unit Cost HTVA": "unitCost",
};

const escapeCsvCell = (value) => {
  const stringValue = value === null || value === undefined ? "" : String(value);
  if (/[;"\n\r]/.test(stringValue)) return `"${stringValue.replace(/"/g, '""')}"`;
  return stringValue;
};

const buildCsv = (headers, rows) => {
  const lines = [
    headers.map(escapeCsvCell).join(CSV_SEPARATOR),
    ...rows.map((row) => headers.map((header) => escapeCsvCell(row[header])).join(CSV_SEPARATOR)),
  ];

  return lines.join("\r\n");
};

const downloadCsv = (filename, headers, rows) => {
  const blob = new Blob([`\uFEFF${buildCsv(headers, rows)}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const getCsvDelimiter = (text) => {
  const firstDataLine = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .find((line) => line.trim());

  if (!firstDataLine) return ",";
  return (firstDataLine.match(/;/g) || []).length > (firstDataLine.match(/,/g) || []).length
    ? ";"
    : ",";
};

const parseCsv = (text) => {
  const rows = [];
  let currentCell = "";
  let currentRow = [];
  let inQuotes = false;
  const normalizedText = text.replace(/^\uFEFF/, "");
  const delimiter = getCsvDelimiter(normalizedText);

  for (let i = 0; i < normalizedText.length; i += 1) {
    const char = normalizedText[i];
    const nextChar = normalizedText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentCell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      currentRow.push(currentCell);
      currentCell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") i += 1;
      currentRow.push(currentCell);
      if (currentRow.some((cell) => String(cell).trim() !== "")) rows.push(currentRow);
      currentRow = [];
      currentCell = "";
      continue;
    }

    currentCell += char;
  }

  currentRow.push(currentCell);
  if (currentRow.some((cell) => String(cell).trim() !== "")) rows.push(currentRow);

  return rows;
};

const parseInventoryCount = (rawValue) => {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return null;

  const compactValue = raw.replace(/\s/g, "");
  let normalizedValue = compactValue;

  if (compactValue.includes(",") && compactValue.includes(".")) {
    // The last separator is treated as the decimal separator, so both
    // 1 000,50 and 1,000.50 remain easy to enter in a spreadsheet.
    if (compactValue.lastIndexOf(",") > compactValue.lastIndexOf(".")) {
      normalizedValue = compactValue.replace(/\./g, "").replace(",", ".");
    } else {
      normalizedValue = compactValue.replace(/,/g, "");
    }
  } else if (compactValue.includes(",")) {
    normalizedValue = compactValue.replace(",", ".");
  }

  if (!/^\d+(?:\.\d+)?$/.test(normalizedValue)) return null;
  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : null;
};

const useStockCsv = ({
  stockItems,
  stockCategories,
  venueId,
  createdBy,
  movementsStartDate,
  movementsEndDate,
}) => {
  const [csvLoader, setCsvLoader] = useState(false);
  const importFileInputRef = useRef(null);
  const inventoryUpdateFileInputRef = useRef(null);
  const restockUpdateFileInputRef = useRef(null);
  const pendingRestockRows = useRef([]);
  const restockBusy = useRef(false);
  const [restockReport, setRestockReport] = useState(null);
  const [restockRetryCount, setRestockRetryCount] = useState(0);
  const alert = useAlert();
  const { t } = useTranslation("stockManagement");
  const movementReasonLabels = {
    sale: t("movements_modal_reason_sale"),
    cancel: t("movements_modal_reason_cancel"),
    restock: t("movements_modal_reason_restock"),
    inventory_adjustment: t("movements_modal_reason_inventory_adjustment"),
    waste: t("movements_modal_reason_waste"),
  };

  const handleProductItemsCsvExport = async () => {
    setCsvLoader(true);
    try {
      const rows = stockItems.map((item) => ({
        ID: item.id || " / ",
        Name: item.name || " / ",
        "Category ID": item.categoryId || " / ",
        Unit: item.unit || " / ",
        "Minimum Alert": item.minAlert ?? " / ",
        "On Hand": item.onHand ?? 0,
        "Unit Cost HTVA": item.unitCost ?? "",
        Update: "",
      }));

      downloadCsv("stock_items.csv", ITEMS_CSV_HEADER, rows);
      alert.show(t("stock_excel_success_items_export"), { type: "success" });
    } catch (error) {
      alert.show(t("stock_excel_error_export"), { type: "error" });
    } finally {
      setCsvLoader(false);
    }
  };

  const handleMovementsCsvExport = async (stockItemId = null) => {
    setCsvLoader(true);

    try {
      const movements = await getStockMovements(
        venueId,
        stockItemId,
        movementsStartDate ? dayjs(movementsStartDate).startOf("day").valueOf() : null,
        movementsEndDate ? dayjs(movementsEndDate).endOf("day").valueOf() : null,
      );

      const rows = movements.map((movement) => ({
        ID: movement.id || " / ",
        "Stock Item ID": movement.stockItemId || " / ",
        Delta: movement.delta ?? " / ",
        "Unit Cost HTVA": movement.unitCost ?? "",
        "Value HTVA": movement.value ?? "",
        Reason: movementReasonLabels[movement.reason] || movement.reason || " / ",
        "Created At": movement.createdAt ? dayjs(movement.createdAt).format(DATE_FORMAT) : " / ",
      }));

      downloadCsv("stock_movements.csv", MOVEMENTS_CSV_HEADER, rows);
      alert.show(t("stock_excel_success_movements_export"), { type: "success" });
    } catch (error) {
      alert.show(t("stock_excel_error_movements_export"), { type: "error" });
    } finally {
      setCsvLoader(false);
    }
  };

  const handleProductItemsCsvImport = async (event, onSuccess) => {
    const file = event.target.files?.[0];
    if (importFileInputRef.current) importFileInputRef.current.value = "";
    if (!file) return;

    setCsvLoader(true);
    const reader = new FileReader();
    reader.onload = async (loadEvent) => {
      try {
        const data = parseCsv(String(loadEvent.target.result || ""));

        if (!data.length || !data[0]?.length) {
          alert.show(t("stock_excel_error_empty_invalid"), { type: "error" });
          return;
        }

        const header = data[0].map((column) => String(column || "").trim());
        if (!Object.keys(STOCK_ITEMS_HEADER_TO_KEYS).filter(key => key !== "Unit Cost HTVA").every((key) => header.includes(key))) {
          alert.show(t("stock_excel_error_invalid_header"), { type: "error" });
          return;
        }

        const rowsToProcess = [];

        data.slice(1).forEach((rawRow) => {
          const row = {};
          header.forEach((currentHeader, columnIndex) => {
            const mappedKey = STOCK_ITEMS_HEADER_TO_KEYS[currentHeader];
            if (mappedKey) row[mappedKey] = rawRow[columnIndex];
          });

          const rowData = {
            id: String(row.id ?? "").trim(),
            name: String(row.name ?? "").trim(),
            unit: String(row.unit ?? "").trim(),
            categoryId: String(row.categoryId ?? "").trim(),
            minAlert: Number(row.minAlert),
            onHand: Number(row.onHand),
            ...(row.unitCost == null || row.unitCost === "" ? {} : { unitCost: parseInventoryCount(row.unitCost) }),
          };

          if (
            (rowData.unitCost !== undefined && (rowData.unitCost == null || rowData.unitCost < 0)) ||
            !rowData.name ||
            !rowData.unit ||
            Number.isNaN(rowData.minAlert) ||
            Number.isNaN(rowData.onHand) ||
            rowData.minAlert < 0 ||
            rowData.onHand < 0 ||
            !rowData.categoryId
          )
            return;

          rowsToProcess.push(rowData);
        });

        if (!rowsToProcess.length) {
          alert.show(t("stock_excel_error_no_valid_rows"), { type: "error" });
          return;
        }

        let createdCount = 0;
        let updatedCount = 0;
        let skippedCount = 0;

        const categoriesIds = stockCategories.map((doc) => doc.id);
        const rowChunks = [];
        for (let i = 0; i < rowsToProcess.length; i += IMPORT_TRANSACTION_CHUNK_SIZE) {
          rowChunks.push(rowsToProcess.slice(i, i + IMPORT_TRANSACTION_CHUNK_SIZE));
        }

        for (const chunk of rowChunks) {
          const existingRefs = chunk
            .filter((row) => row.id && categoriesIds.includes(row.categoryId))
            .map((row) => ({ row, ref: doc(db, "StockItems", row.id) }));

          const now = Date.now();
          await runTransaction(db, async (transaction) => {
            const snapshots = await Promise.all(
              existingRefs.map(({ ref }) => transaction.get(ref)),
            );

            snapshots.forEach((snap, i) => {
              const { row, ref } = existingRefs[i];
              if (!snap.exists()) {
                skippedCount += 1;
                return;
              }

              const existingData = snap.data() || {};
              const currentOnHand = Number.isFinite(Number(existingData.onHand))
                ? Number(existingData.onHand)
                : 0;
              const nextOnHand = Number.isFinite(Number(row.onHand)) ? Number(row.onHand) : 0;

              if (
                existingData.categoryId === row.categoryId &&
                Number(existingData.minAlert) === Number(row.minAlert) &&
                existingData.name === row.name &&
                existingData.unit === row.unit &&
                (row.unitCost === undefined || existingData.unitCost === row.unitCost) &&
                currentOnHand === nextOnHand
              )
                return;

              transaction.set(
                ref,
                {
                  categoryId: row.categoryId,
                  minAlert: row.minAlert,
                  name: row.name,
                  unit: row.unit,
                  ...(row.unitCost === undefined ? {} : { unitCost: row.unitCost }),
                  onHand: row.onHand,
                  updatedAt: now,
                },
                { merge: true },
              );

              if (currentOnHand !== nextOnHand) {
                transaction.set(doc(collection(db, "StockMovements")), {
                  createdAt: now,
                  createdBy: createdBy,
                  delta: nextOnHand - currentOnHand,
                  ...stockMovementValue({ ...existingData, ...(row.unitCost === undefined ? {} : { unitCost: row.unitCost }) }, nextOnHand - currentOnHand),
                  reason: "inventory_adjustment",
                  stockItemId: row.id,
                  venueId,
                });
              }
              updatedCount += 1;
            });

            chunk
              .filter((row) => !row.id && categoriesIds.includes(row.categoryId))
              .forEach((row) => {
                const newRef = doc(collection(db, "StockItems"));
                transaction.set(newRef, {
                  venueId,
                  categoryId: row.categoryId,
                  minAlert: row.minAlert,
                  name: row.name,
                  unit: row.unit,
                  updatedAt: now,
                  ...(row.unitCost === undefined ? {} : { unitCost: row.unitCost }),
                  onHand: row.onHand,
                  totalPrice: 0,
                });

                if (row.onHand) {
                  transaction.set(doc(collection(db, "StockMovements")), {
                    createdAt: now,
                    createdBy: createdBy,
                    delta: row.onHand,
                    ...stockMovementValue(row, row.onHand),
                    reason: "inventory_adjustment",
                    stockItemId: newRef.id,
                    venueId,
                  });
                }

                createdCount += 1;
              });
          });
        }

        if (!createdCount && !updatedCount && !skippedCount) {
          alert.show(t("stock_excel_error_no_valid_rows"), { type: "error" });
          return;
        }
        alert.show(t("stock_excel_success_import", { createdCount, updatedCount, skippedCount }), {
          type: "success",
        });
        if (typeof onSuccess === "function") await onSuccess();
      } catch (error) {
        alert.show(t("stock_excel_error_import"), { type: "error" });
      } finally {
        setCsvLoader(false);
      }
    };

    reader.onerror = () => {
      alert.show(t("stock_excel_error_read"), { type: "error" });
      setCsvLoader(false);
    };

    reader.readAsText(file);
  };

  const handleInventoryUpdateCsvImport = async (event, onSuccess) => {
    const file = event.target.files?.[0];
    if (inventoryUpdateFileInputRef.current) inventoryUpdateFileInputRef.current.value = "";
    if (!file) return;

    setCsvLoader(true);
    const reader = new FileReader();
    reader.onload = async (loadEvent) => {
      try {
        const data = /\.numbers$/i.test(file.name)
          ? (() => {
              const workbook = XLSX.read(new Uint8Array(loadEvent.target.result), { type: "array" });
              const sheet = workbook.Sheets[workbook.SheetNames[0]];
              if (!sheet) return [];
              return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false })
                .map((row) => row.map((value) =>
                  // Numbers can decode decimals with insignificant floating-point artifacts.
                  typeof value === "number" && Number.isFinite(value)
                    ? Number(value.toPrecision(15))
                    : value,
                ));
            })()
          : parseCsv(String(loadEvent.target?.result || ""));
        const header = (data[0] || []).map((column) => String(column || "").trim());
        const idIndex = header.indexOf("ID");
        const updateIndex = header.indexOf("Update") >= 0 ? header.indexOf("Update") : header.indexOf("Updated");

        if (!data.length || idIndex < 0 || updateIndex < 0) {
          alert.show(t("stock_inventory_update_invalid_header"), { type: "error" });
          return;
        }

        const stockItemIds = new Set(stockItems.map((item) => item.id));
        const importRows = data.slice(1);
        const rowsToCount = importRows.reduce((rows, row) => {
          const stockItemId = String(row[idIndex] ?? "").trim();
          const countedValue = parseInventoryCount(row[updateIndex]);

          if (stockItemId && stockItemIds.has(stockItemId) && countedValue !== null) {
            rows.push({ stockItemId, countedValue });
          }
          return rows;
        }, []);

        if (!rowsToCount.length) {
          alert.show(t("stock_inventory_update_no_valid_rows"), { type: "error" });
          return;
        }

        let updatedCount = 0;
        let skippedCount = importRows.length - rowsToCount.length;
        const inventoryChunks = [];
        for (let index = 0; index < rowsToCount.length; index += 10) {
          inventoryChunks.push(rowsToCount.slice(index, index + 10));
        }

        for (const chunk of inventoryChunks) {
          const results = await Promise.allSettled(
            chunk.map(({ stockItemId, countedValue }) =>
              inventoryCountStockItem({ stockItemId, countedValue, venueId, createdBy }),
            ),
          );

          results.forEach((result, index) => {
            if (result.status === "fulfilled") {
              updatedCount += 1;
              return;
            }
            skippedCount += 1;
            console.error(
              `Unable to apply inventory count for stock item ${chunk[index].stockItemId}:`,
              result.reason,
            );
          });
        }

        alert.show(t("stock_inventory_update_success", { updatedCount, skippedCount }), {
          type: skippedCount ? "warning" : "success",
        });
        if (updatedCount && typeof onSuccess === "function") await onSuccess();
      } catch (error) {
        console.error("Unable to import inventory update:", error);
        alert.show(t("stock_inventory_update_error"), { type: "error" });
      } finally {
        setCsvLoader(false);
      }
    };

    reader.onerror = () => {
      alert.show(t("stock_excel_error_read"), { type: "error" });
      setCsvLoader(false);
    };

    if (/\.numbers$/i.test(file.name)) reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
  };

  const retryRestockImport = async (onSuccess) => {
    if (restockBusy.current || !pendingRestockRows.current.length) return;
    restockBusy.current = true;
    setCsvLoader(true);
    let updatedCount = 0;
    const failed = [];
    try {
      const rows = pendingRestockRows.current;
      for (let index = 0; index < rows.length; index += 10) {
        const chunk = rows.slice(index, index + 10);
        const results = await Promise.allSettled(chunk.map(row => restockStockItem({ ...row, venueId, createdBy })));
        updatedCount += results.filter(result => result.status === 'fulfilled').length;
        failed.push(...chunk.filter((row, i) => results[i].status === 'rejected'));
      }
      pendingRestockRows.current = failed;
      setRestockRetryCount(failed.length);
      setRestockReport(previous => ({ ...previous,
        updatedCount: (previous?.updatedCount || 0) + updatedCount,
        failedLines: failed.map(row => row.line),
      }));
      alert.show(t('stock_restock_update_success', { updatedCount, skippedCount: failed.length }), { type: failed.length ? 'warning' : 'success' });
      if (updatedCount && typeof onSuccess === 'function') await onSuccess();
    } finally {
      restockBusy.current = false;
      setCsvLoader(false);
    }
  };

  const handleRestockUpdateCsvImport = async (event, onSuccess) => {
    const file = event.target.files?.[0];
    if (restockUpdateFileInputRef.current) restockUpdateFileInputRef.current.value = '';
    if (!file || restockBusy.current || pendingRestockRows.current.length) return;
    setCsvLoader(true);
    try {
      const parsed = parseRestockRows(parseCsv(await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = reject;
        reader.readAsText(file);
      })), stockItems, parseInventoryCount);
      const batchId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      pendingRestockRows.current = parsed.rows.map(row => ({ ...row, operationId: `${batchId}:${row.line}` }));
      setRestockReport({ updatedCount: 0, invalidLines: parsed.errors.map(row => row.line), ignored: parsed.ignored, failedLines: [] });
      if (!parsed.rows.length) {
        alert.show(t('stock_restock_update_no_valid_rows'), { type: 'warning' });
      } else await retryRestockImport(onSuccess);
    } catch (error) {
      alert.show(t(error.message === 'INVALID_RESTOCK_HEADERS' ? 'stock_restock_update_invalid_header' : 'stock_restock_update_error'), { type: 'error' });
    } finally { setCsvLoader(false); }
  };

  return {
    restockReport, restockRetryCount, retryRestockImport,
    csvLoader,
    importFileInputRef,
    inventoryUpdateFileInputRef,
    restockUpdateFileInputRef,
    handleProductItemsCsvExport,
    handleProductItemsCsvImport,
    handleInventoryUpdateCsvImport,
    handleRestockUpdateCsvImport,
    handleMovementsCsvExport,
  };
};

export default useStockCsv;
